import dayjs from 'dayjs';
import Text from '../../../components/detail/components/Text.component';
import { Box, Chip, Stack, Typography } from '@mui/material';
import StarsDisplay from '../../../components/starsComponent/starsDisplay.component';
import _ from 'lodash';

const detailLabelStyle = {
  textTransform: 'capitalize',
  whiteSpace: 'nowrap',
  pr: 1,
  fontWeight: 'bold',
  color: '#446080',
};

const renderAlignedField = (label, content) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      alignItems: { xs: 'flex-start', md: 'center' },
      width: '100%',
    }}
  >
    <Typography sx={detailLabelStyle}>{`${label}:`}</Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, minWidth: 0 }}>
      {content}
    </Box>
  </Box>
);

const renderTagsField = (label, data = []) => {
  const safeData = Array.isArray(data) ? data : [];

  return renderAlignedField(
    label,
    !_.isEmpty(safeData) ? (
      safeData.map((item, index) => (
        <Chip
          key={item?.id || `${label}-${item?.attributes?.name}-${index}`}
          label={item?.attributes?.name}
          size='small'
          sx={{ height: 24 }}
        />
      ))
    ) : (
      <Typography color='#446080' variant='subtitle1'>
        {' - '}
      </Typography>
    )
  );
};

const renderSupplierContactsField = (contacts = []) => {
  const safeContacts = Array.isArray(contacts) ? contacts : [];
  const filledContacts = safeContacts
    .map((contact) => ({
      main_contact: _.isString(contact?.main_contact)
        ? contact.main_contact.trim()
        : null,
      main_contact_phone_number: _.isString(contact?.main_contact_phone_number)
        ? contact.main_contact_phone_number.trim()
        : null,
      main_contact_email: _.isString(contact?.main_contact_email)
        ? contact.main_contact_email.trim()
        : null,
    }))
    .filter(
      (contact) =>
        contact?.main_contact ||
        contact?.main_contact_phone_number ||
        contact?.main_contact_email
    );

  if (!filledContacts.length) {
    return null;
  }

  return !_.isEmpty(filledContacts) ? (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <Typography sx={detailLabelStyle}>Supplier Contacts:</Typography>
      {filledContacts.map((contact, index) => (
        <Box
          key={`supplier-contact-${index}`}
          sx={{
            px: 1.25,
            py: 1,
          }}
        >
          {contact?.main_contact && (
            <Typography color='#446080' variant='body2'>
              {contact.main_contact}
            </Typography>
          )}

          {contact?.main_contact_phone_number && (
            <Typography color='text.secondary' variant='body2'>
              {`Phone: ${contact.main_contact_phone_number}`}
            </Typography>
          )}

          {contact?.main_contact_email && (
            <Typography color='text.secondary' variant='body2'>
              {`E-mail: ${contact.main_contact_email}`}
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  ) : null;
};

export const DetailConf = {
  initialState: {
    loading: false,
    error: null,
    data: null,
    meta: null,
  },
  fields: [
    {
      id: 'supplier_section',
      type: 'section',
      label: 'Mexal Info',
      xs: 12,
      items: [
        {
          type: 'text',
          label: 'Supplier',
          key: 'attributes.label',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Department',
          key: 'attributes.id_departments',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Category',
          key: 'attributes.id_category',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Sub-Category',
          key: 'attributes.id_sub_category',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Location',
          key: 'attributes.id_locations',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Address',
          key: 'attributes.address',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Main Phone Number',
          key: 'attributes.phone_number',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'E-mail',
          key: 'attributes.email',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Website',
          key: 'attributes.website',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Priority',
          key: 'attributes.priority',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Mexal',
          key: 'attributes.mexal_code',
          xs: 12,
          md: 6,
        },
        { type: 'text', label: 'Year', key: 'attributes.year', xs: 12, md: 6 },
      ],
    },
    {
      id: 'supplier_contacts',
      type: 'section',
      label: '',
      xs: 12,
      items: [
        {
          type: 'text',
          label: '',
          key: 'attributes.supplier_contacts',
          xs: 12,
          md: 12,
        },
        // { type: 'text', label: "Status", key: 'attributes.status', xs:12, md:6 },
      ],
    },
    {
      id: 'operational_details',
      type: 'section',
      label: 'Operational Details',
      xs: 12,
      items: [
        {
          type: 'text',
          label: 'Opening Date',
          key: 'attributes.opening_date',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Closing Date',
          key: 'attributes.closing_date',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Other languages',
          key: 'attributes.id_languages',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Target',
          key: 'attributes.target',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Details',
          key: 'attributes.details',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Stars',
          key: 'attributes.stars',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Services On Board',
          key: 'attributes.services_on_board',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Working Hours',
          key: 'attributes.working_hours',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Fares',
          key: 'attributes.fares',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Commissions',
          key: 'attributes.commissions',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Cancellation Fees',
          key: 'attributes.cancellation_fees',
          xs: 12,
          md: 6,
        },
        // { type: 'text', label: 'Attachments', key: 'attributes.attachments', xs:12, md:6 },
        {
          type: 'text',
          label: 'Last Update',
          key: 'attributes.last_update',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Notes',
          key: 'attributes.notes',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Rules',
          key: 'attributes.rules',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Other Contacts',
          key: 'attributes.other_contacts',
          xs: 12,
          md: 6,
        },
      ],
    },
    {
      id: 'invoicing_details',
      type: 'section',
      label: 'Invoicing Details',
      xs: 12,
      items: [
        {
          type: 'text',
          label: 'Company Name',
          key: 'attributes.company_name',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Invoicing Address',
          key: 'attributes',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Vat Number',
          key: 'attributes.vat_number',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Codice Univoco (SDI)',
          key: 'attributes.sdi_code',
          xs: 12,
          md: 6,
        },
        {
          type: 'text',
          label: 'Bank Details',
          key: 'attributes.bank_details',
          xs: 12,
          md: 6,
        },
      ],
    },
  ],
  formatValue: (key, value) => {
    switch (key) {
      case 'attributes':
        const addressParts = [
          value.address,
          value.zip_code,
          value.location,
          value.state,
          value.country,
        ].filter((part) => part && part.trim() !== '');
        return renderAlignedField(
          'Invoicing Address',
          <Typography color='#446080' variant='subtitle1'>
            {!_.isEmpty(addressParts) ? addressParts.join(' - ') : ' - '}
          </Typography>
        );
      case 'attributes.year':
        return <Text label='Year' value={dayjs(value).format('YYYY')} />;
      case 'attributes.id_locations':
        return (
          <Text
            label='Location'
            value={
              value?.data.map((item) => item?.attributes?.marina).join(' - ') ||
              ' - '
            }
          />
        );
      case 'attributes.id_departments':
        return (
          <Text
            label='Department'
            value={
              value?.data
                .map((item) => item?.attributes?.name_department)
                .join(' - ') || ' - '
            }
          />
        );
      case 'attributes.opening_date':
        return (
          <Text
            label='Opening Date'
            value={dayjs(value).format('DD-MM-YYYY')}
          />
        );
      case 'attributes.closing_date':
        return (
          <Text
            label='Closing Date'
            value={dayjs(value).format('DD-MM-YYYY')}
          />
        );
      case 'attributes.last_update':
        return (
          <Text
            label='Last Update'
            value={`${dayjs(value).format('DD-MM-YYYY')} - ${dayjs().format('HH:mm')}`}
          />
        );
      case 'attributes.id_languages':
        return renderAlignedField(
          'Other Languages',
          <Typography color='#446080' variant='subtitle1'>
            {value.data
              .map(
                (item) =>
                  `${item?.attributes?.nation} (${item?.attributes?.language})`
              )
              .join(' - ') || ' - '}
          </Typography>
        );
      case 'attributes.supplier_contacts':
        return renderSupplierContactsField(value);
      case 'attributes.stars':
        return (
          <Box sx={{ display: 'flex' }}>
            <Text label='Stars' />
            <StarsDisplay value={value} />
          </Box>
        );
      case 'attributes.id_category':
        return renderTagsField('Category', value?.data);
      case 'attributes.id_sub_category':
        return renderTagsField('Sub Category', value?.data);
      default:
        return null;
    }
  },
};
